const PROD_APP_ORIGIN = 'https://app.ewertcards.com'
const PROD_CARD_ORIGIN = 'https://card.ewertcards.com'
const PROD_FALLBACK_API = 'https://offline.api.ewertcards.com/'
const PROD_APP_HOSTNAME = 'app.ewertcards.com'
const PROD_CARD_HOSTNAME = 'card.ewertcards.com'

function hasWindow() {
  return typeof window !== 'undefined'
}
 
function getBrowserOrigin() {
  return hasWindow() ? window.location.origin : ''
}

function getBrowserHostname() {
  return hasWindow() ? window.location.hostname : ''
}

function normalizeOrigin(value, fallback = '') {
  const resolved = (value || fallback || '').trim()
  return resolved.replace(/\/+$/, '')
}

function normalizeBaseURL(value, fallback = '') {
  const resolved = (value || fallback || '').trim()
  if (!resolved) return ''
  return resolved.endsWith('/') ? resolved : `${resolved}/`
}

function isLocalHostname(hostname) {
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '0.0.0.0'
    || hostname.endsWith('.local')
}

function swapSubdomain(origin, fromSubdomain, toSubdomain) {
  return normalizeOrigin(String(origin || '').replace(`://${fromSubdomain}.`, `://${toSubdomain}.`))
}

function isProdHostname(hostname) {
  return hostname === PROD_APP_HOSTNAME || hostname === PROD_CARD_HOSTNAME
}

function resolveAppOrigin({ browserOrigin, browserHostname, isLocal }) {
  if (process.env.VUE_APP_APP_ORIGIN) {
    return normalizeOrigin(process.env.VUE_APP_APP_ORIGIN)
  }

  if (isLocal) {
    return normalizeOrigin(browserOrigin, 'http://localhost:8080')
  }

  if (browserHostname.startsWith('app.')) {
    return normalizeOrigin(browserOrigin, PROD_APP_ORIGIN)
  }

  if (browserHostname.startsWith('card.')) {
    return swapSubdomain(browserOrigin, 'card', 'app') || PROD_APP_ORIGIN
  }

  return normalizeOrigin(browserOrigin, PROD_APP_ORIGIN)
}

function resolveCardOrigin({ browserOrigin, browserHostname, isLocal }) {
  if (process.env.VUE_APP_CARD_ORIGIN) {
    return normalizeOrigin(process.env.VUE_APP_CARD_ORIGIN)
  }

  if (isLocal) {
    return normalizeOrigin(browserOrigin, 'http://localhost:8080')
  }

  if (browserHostname.startsWith('card.')) {
    return normalizeOrigin(browserOrigin, PROD_CARD_ORIGIN)
  }

  if (browserHostname.startsWith('app.')) {
    return swapSubdomain(browserOrigin, 'app', 'card') || PROD_CARD_ORIGIN
  }

  return normalizeOrigin(browserOrigin, PROD_CARD_ORIGIN)
}

function resolveFallbackApiBaseURL({ browserHostname, isLocal }) {
  if (process.env.VUE_APP_API_FALLBACK_URL) {
    return normalizeBaseURL(process.env.VUE_APP_API_FALLBACK_URL)
  }

  if (isLocal || !isProdHostname(browserHostname)) {
    return ''
  }

  return normalizeBaseURL(PROD_FALLBACK_API)
}

function joinOriginPath(origin, path = '') {
  const safeOrigin = normalizeOrigin(origin)
  const safePath = String(path || '').replace(/^\/+/, '')
  return safePath ? `${safeOrigin}/${safePath}` : safeOrigin
}

const browserOrigin = getBrowserOrigin()
const browserHostname = getBrowserHostname()
const isLocalDevelopment = isLocalHostname(browserHostname)

const appOrigin = resolveAppOrigin({ browserOrigin, browserHostname, isLocal: isLocalDevelopment })
const cardOrigin = resolveCardOrigin({ browserOrigin, browserHostname, isLocal: isLocalDevelopment })

export const runtimeConfig = {
  browserOrigin,
  browserHostname,
  isLocalDevelopment,
  appOrigin,
  cardOrigin,
  staticCardsOrigin: normalizeOrigin(process.env.VUE_APP_STATIC_CARDS_ORIGIN, cardOrigin),
  apiPrimaryBaseURL: normalizeBaseURL(process.env.VUE_APP_API_BASE_URL, '/api'),
  apiFallbackBaseURL: resolveFallbackApiBaseURL({
    browserHostname,
    isLocal: isLocalDevelopment
  }),
  apiPingPath: process.env.VUE_APP_API_PING_PATH || 'api/version'
}

export { joinOriginPath, normalizeBaseURL, normalizeOrigin }
